import { firstValueFrom } from 'rxjs';

import { AVAILABILITY_TIMEOUT_MS, AvailabilityOutcome, availabilityInputsOf, availabilityOutcomeOf } from '../utils/upload-expiry';

/** The slice of `LightHttp` this needs, so the spec can hand in a stub. */
export interface AvailabilityHttp {
    request<T>(url: string, method: 'POST', body: unknown, options?: { headers?: Record<string, string>; timeout?: number }): { response$: import('rxjs').Observable<T> };
}

/**
 * FIX-2 T2 (UX-SPEC §2.1): ask the server whether a card's upload still exists before Re-edit opens the modal.
 *
 * Capped at `timeoutMs` (3 s) by a timer that does not trust the transport's own timeout, and FAIL OPEN: a network error,
 * a timeout, a non-2xx, an `unknown` state or a malformed body is `check_failed`, and the caller opens the modal anyway
 * -- the submit-time 410 is the backstop. Never throws. The upload tokens travel in the POST body only.
 */
export async function checkUploadAvailability(
    card: Parameters<typeof availabilityInputsOf>[0],
    http: AvailabilityHttp,
    url: string,
    headers: Record<string, string> = {},
    timeoutMs: number = AVAILABILITY_TIMEOUT_MS,
): Promise<AvailabilityOutcome> {
    const inputs = availabilityInputsOf(card);
    if (inputs.length === 0) return 'check_failed';
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timedOut = new Promise<AvailabilityOutcome>(resolve => {
        timer = setTimeout(() => resolve('check_failed'), Math.max(0, timeoutMs));
    });
    const asked = (async (): Promise<AvailabilityOutcome> => {
        try {
            const body = await firstValueFrom(http.request<unknown>(url, 'POST', { inputs }, {
                headers: { 'Content-Type': 'application/json', ...headers },
                timeout: timeoutMs,
            }).response$);
            return availabilityOutcomeOf(body, inputs.length);
        } catch {
            return 'check_failed';
        }
    })();
    try {
        return await Promise.race([asked, timedOut]);
    } finally {
        if (timer) clearTimeout(timer);
    }
}
